import React from "react";
import { Mail, ExternalLink, Clock, HelpCircle, LogIn, Search, MessageCircle, User, Shield, ArrowRight, CheckCircle2, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

// Mock translation function for demo purposes
const useTranslation = () => {
  const t = (key) => {
    const translations = {
      "Grievance Officer": "Grievance Officer",
      "Muthukumar": "Muthukumar",
      "Email": "Email",
      "You can also visit": "You can also visit",
      "this page": "this page",
      "for contact information.": "for contact information.",
      "We're available 24*7 to assist you": "We're available 24×7 to assist you",
      "If you have enrolled as a learner to any of our online learning programs and have a course related query, please follow the below steps for help.": "If you have enrolled as a learner to any of our online learning programs and have a course related query, please follow the below steps for help.",
      "Step 1": "Step 1",
      "Log in to your": "Log in to your",
      "Learning Platform": "Learning Platform",
      ", using your registered Email & Password.": ", using your registered Email & Password.",
      "Step 2": "Step 2",
      "Select the Help icon at the top right corner.": "Select the Help icon at the top right corner.",
      "Step 3": "Step 3",
      "Browse through the FAQs and their answers.": "Browse through the FAQs and their answers.",
      "Step 4": "Step 4",
      "To create a support request, click on 'Connect to yslearn' and follow the instructions.": "To create a support request, click on 'Connect to yslearn' and follow the instructions."
    };
    return translations[key] || key;
  };
  return { t };
};

export default function GrievanceOfficerPage() {
  const { t } = useTranslation();

  const steps = [
    {
      id: 1,
      title: t("Log in to your"),
      description: t(", using your registered Email & Password."),
      linkText: t("Learning Platform"),
      icon: LogIn,
      color: "from-blue-500 to-cyan-500",
      bgColor: "from-blue-50 to-cyan-50",
      shadowColor: "shadow-blue-200"
    },
    {
      id: 2,
      title: t("Select the Help icon"),
      description: t("at the top right corner."),
      icon: HelpCircle,
      color: "from-purple-500 to-indigo-500",
      bgColor: "from-purple-50 to-indigo-50",
      shadowColor: "shadow-purple-200"
    },
    {
      id: 3,
      title: t("Browse through the FAQs"),
      description: t("and their answers."),
      icon: Search,
      color: "from-green-500 to-emerald-500",
      bgColor: "from-green-50 to-emerald-50",
      shadowColor: "shadow-green-200"
    },
    {
      id: 4,
      title: t("Create a support request"),
      description: t("click on 'Connect to yslearn' and follow the instructions."),
      icon: MessageCircle,
      color: "from-orange-500 to-red-500",
      bgColor: "from-orange-50 to-red-50",
      shadowColor: "shadow-orange-200"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="!absolute inset-0">
        <div className="!absolute top-20 left-20 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="!absolute top-40 right-20 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="!absolute bottom-20 left-1/3 w-80 h-80 bg-cyan-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Background Pattern */}
      <div className="!absolute inset-0 opacity-30">
        <div className="!absolute inset-0" 
             style={{
               backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236366f1' fill-opacity='0.1'%3E%3Cpath d='M40 40c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm20 0c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
             }}>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 md:py-12 lg:py-16">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-white/80 backdrop-blur-sm border border-blue-200 text-blue-800 px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-lg">
            <Shield className="w-5 h-5 mr-2" />
            {t("Grievance Officer")} Support Center
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Professional <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">Support</span>
          </h1>
          <p className="text-xl !text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Dedicated assistance for all your learning platform needs with professional grievance resolution
          </p>
        </div>

        {/* Grievance Officer Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-8 lg:p-12 mb-16 relative overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="!absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-100/50 to-purple-100/50 rounded-full -translate-y-20 translate-x-20"></div>
          <div className="!absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-cyan-100/50 to-blue-100/50 rounded-full translate-y-16 -translate-x-16"></div>
          
          <div className="relative z-10">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              
              {/* Officer Information */}
              <div className="lg:col-span-2">
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl flex items-center justify-center shadow-xl">
                    <User className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-4">
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        {t("Muthukumar")}
                      </h2>
                      <p className="text-xl !text-gray-600 font-semibold mb-1">{t("Grievance Officer")}</p>
                      <p className="!text-gray-500">yslearn </p>
                    </div>
                    
                    {/* Contact Information */}
                    <div className="space-y-3">
                      <div className="flex items-center !text-gray-700 group">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm !text-gray-500 font-medium">{t("Email")}</p>
                          <a 
                            href="mailto:rlm.muthukumar@gmail.com" 
                            className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                          >
                            rlm.muthukumar@gmail.com
                          </a>
                        </div>
                      </div>
                      
                      <div className="flex items-center !text-gray-700">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                          <ExternalLink className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm !text-gray-500 font-medium">Additional Info</p>
                          <p className="!text-gray-700">
                            {t("You can also visit")}{" "}
                            <Link
                              to={"/contact"}
                              className="text-blue-600 hover:text-blue-800 font-semibold underline decoration-2 underline-offset-2 transition-colors"
                            >
                              {t("this page")}
                            </Link>{" "}
                            {t("for contact information.")}.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="lg:col-span-1">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <Clock className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-gray-900 mb-1">24/7</div>
                    <div className="text-blue-600 text-sm font-semibold">Available</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-gray-900 mb-1">48hr</div>
                    <div className="text-green-600 text-sm font-semibold">Response</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Assistance Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t("We're available 24*7 to assist you").replace('24*7', '24×7')}
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg !text-gray-600 leading-relaxed mb-8">
              {t("If you have enrolled as a learner to any of our online learning programs and have a course related query, please follow the below steps for help.")}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </div>
        </div>

        {/* Steps Section */}
        <div className="max-w-5xl mx-auto">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div key={step.id} className="group relative">
                <div className="flex flex-col lg:flex-row items-start gap-8">
                  
                  {/* Step Indicator */}
                  <div className="flex-shrink-0 relative">
                    <div className={`w-20 h-20 bg-gradient-to-r ${step.color} rounded-3xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-all duration-500`}>
                      <step.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="!absolute -top-3 -right-3 w-10 h-10 bg-white rounded-2xl flex items-center justify-center shadow-lg border-4 border-gray-50">
                      <span className="text-lg font-bold !text-gray-700">{step.id}</span>
                    </div>
                    
                    {/* Connecting Line */}
                    {index < steps.length - 1 && (
                      <div className="hidden lg:block !absolute top-24 left-1/2 transform -translate-x-1/2 w-1 h-20 bg-gradient-to-b from-gray-300 via-gray-200 to-transparent rounded-full"></div>
                    )}
                  </div>

                  {/* Step Content */}
                  <div className={`flex-1 bg-gradient-to-r ${step.bgColor} border border-white/60 rounded-3xl p-8 shadow-xl ${step.shadowColor} hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 relative overflow-hidden`}>
                    {/* Decorative element */}
                    <div className="!absolute top-0 right-0 w-24 h-24 bg-white/20 rounded-full -translate-y-12 translate-x-12"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                          <span className="!text-gray-500 mr-3">{t(`Step ${step.id}`)}:</span>
                          <span className="text-gray-800">{step.title}</span>
                          {step.linkText && (
                            <Link to={'/login'} className="text-blue-600 hover:text-blue-800 font-bold underline decoration-2 underline-offset-4 ml-2 transition-colors">
                              {step.linkText}
                            </Link>
                          )}
                        </h3>
                        <ArrowRight className="w-6 h-6 text-gray-400 group-hover:!text-gray-600 group-hover:translate-x-2 transition-all duration-300 flex-shrink-0" />
                      </div>
                      
                      <p className="text-lg !text-gray-700 leading-relaxed mb-6">
                        {step.description}
                      </p>
                      
                      {/* Progress Indicator */}
                      <div className="w-full h-2 bg-white/60 rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-gradient-to-r ${step.color} rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-1000 origin-left`}
                          style={{ animationDelay: `${index * 200}ms` }}
                        ></div>
                      </div>
                      
                      {/* Step completion indicator */}
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-sm font-semibold !text-gray-600">
                          Follow this step
                        </span>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="mt-20 text-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-12 max-w-3xl mx-auto relative overflow-hidden">
            {/* Background decoration */}
            <div className="!absolute inset-0 bg-gradient-to-r from-blue-50/50 via-purple-50/50 to-indigo-50/50 rounded-3xl"></div>
            
            <div className="relative z-10">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl">
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Need Immediate Assistance?
              </h3>
              <p className="text-lg !text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
                Can't find what you're looking for? Our dedicated support team is ready to help you with any questions or concerns about your learning journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to={'/contact'}>
                <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl">
                  Contact Support Now
                </button>
                </Link>
                <button className="px-8 py-4 bg-white border-2 border-gray-200 !text-gray-700 font-bold rounded-2xl hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-lg">
                  Browse FAQ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}